import { z } from "zod";
import {
  ADS_CAMPAIGN_GOOGLE_BUSINESS_NAME_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_FIRST_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_FIRST_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_MAX_DESCRIPTIONS,
  ADS_CAMPAIGN_GOOGLE_MAX_IMAGES_ASSETS_GROUP,
  ADS_CAMPAIGN_GOOGLE_MAX_LONG_HEADLINES,
  ADS_CAMPAIGN_GOOGLE_MAX_SEARCH_THEMES,
  ADS_CAMPAIGN_GOOGLE_MAX_TITLES,
  ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP,
  ADS_CAMPAIGN_GOOGLE_MIN_DESCRIPTIONS,
  ADS_CAMPAIGN_GOOGLE_MIN_IMAGES_ASSETS_GROUP,
  ADS_CAMPAIGN_GOOGLE_MIN_LONG_HEADLINES,
  ADS_CAMPAIGN_GOOGLE_MIN_TITLES,
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_LONG_HEADLINE_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  GOOGLE,
} from "../../../../../utils/constants";
import i18next from "i18next";
import {
  FIELD_CONSECUTIVE_UPPERCASE,
  FIELD_EMOJI_NOT_ALLOWED,
  FIELD_IMAGES_REPEATED,
  FIELD_IMAGES_MIN_REQUIRED,
  FIELD_MAX_IMAGES_VALID,
  FIELD_MAX_LENGTH,
  FIELD_MAX_TEXTS_VALID,
  FIELD_MAX_VIDEOS_YOUTUBE_VALID,
  FIELD_MIN_TEXTS_VALID,
  FIELD_REQUIRED,
  FIELD_SEARCH_THEMES_MAX_WORDS,
  FIELD_TEXT_REPEATED,
  FIELD_URL_VALID_WITH_PROTOCOL,
  FIELD_VIDEOS_REPEATED,
  FIELD_VIDEO_YOUTUBE_URL_INVALID,
} from "../../../../../i18n/keysTranslations";
import {
  REGEX_URL_WITH_PROTOCOL_REQUIRED,
  REGEX_YOUTUBE_URL,
  THREE_UPPERCASE_REGEX,
} from "../../../../../utils/regex";
import emojiRegex from "emoji-regex";

// Create emoji regex instance
const emojiRegexInstance = emojiRegex();

// Function to validate that text doesn't contain emojis
function validateNoEmojis(text) {
  if (!text || typeof text !== "string") return true;
  return !emojiRegexInstance.test(text);
}

function getIndicesMap(arrayOfStrings) {
  const map = {};
  arrayOfStrings.forEach((txt, i) => {
    if (!map[txt]) {
      map[txt] = [];
    }
    map[txt].push(i);
  });
  return map;
}

export const assetsGroupGoogleBaseSchema = z.object({
  platform: z.literal(GOOGLE),
  id: z.string(),
  destinationUrl: z.any().optional(),
  businessName: z.any().optional(),
  logoUrls: z.any().optional(),
  imageUrls: z.any().optional(),
  videoUrls: z.any().optional(),
  longHeadlines: z.any().optional(),
  descriptions: z.any().optional(),
  titles: z.any().optional(),
  searchThemes: z.any().optional(),
});

export const validateAssetsGroupGoogle = ({
  campaign,
  assetsGroupID,
  assetsGroup,
  ctx,
}) => {
  const platformData = campaign.platformsData?.[GOOGLE];
  const brandGuidelinesEnabled = platformData?.brandGuidelinesEnabled;
  const basicPaths = ["assetsGroups", assetsGroupID];
  // destinationUrl
  if (
    !assetsGroup.destinationUrl ||
    typeof assetsGroup.destinationUrl !== "string" ||
    assetsGroup.destinationUrl.trim() === ""
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "destinationUrl"],
      message: i18next.t(FIELD_REQUIRED),
    });
  } else {
    if (
      !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(
        encodeURI(assetsGroup.destinationUrl)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "destinationUrl"],
        message: i18next.t(FIELD_URL_VALID_WITH_PROTOCOL),
      });
    }
  }

  // businessName
  if (
    (!assetsGroup.businessName ||
      typeof assetsGroup.businessName !== "string" ||
      assetsGroup.businessName.trim() === "") &&
    !brandGuidelinesEnabled
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "businessName"],
      message: i18next.t(FIELD_REQUIRED),
    });
  } else if (
    typeof assetsGroup.businessName === "string" &&
    assetsGroup.businessName.length >
      ADS_CAMPAIGN_GOOGLE_BUSINESS_NAME_MAX_LENGTH &&
    !brandGuidelinesEnabled
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "businessName"],
      message: i18next.t(FIELD_MAX_LENGTH, {
        value: ADS_CAMPAIGN_GOOGLE_BUSINESS_NAME_MAX_LENGTH,
      }),
    });
  } else if (
    !validateNoEmojis(assetsGroup.businessName) &&
    !brandGuidelinesEnabled
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "businessName"],
      message: i18next.t(FIELD_EMOJI_NOT_ALLOWED),
    });
  }

  // logoUrls
  if (
    (!Array.isArray(assetsGroup.logoUrls) || assetsGroup.logoUrls.length < 1) &&
    !brandGuidelinesEnabled
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "logoUrls"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  // imageUrls
  if (
    !Array.isArray(assetsGroup.imageUrls) ||
    assetsGroup.imageUrls.length < ADS_CAMPAIGN_GOOGLE_MIN_IMAGES_ASSETS_GROUP
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "imageUrls"],
      message: i18next.t(FIELD_IMAGES_MIN_REQUIRED, {
        value: ADS_CAMPAIGN_GOOGLE_MIN_IMAGES_ASSETS_GROUP,
      }),
    });
  } else if (
    assetsGroup?.imageUrls?.length > ADS_CAMPAIGN_GOOGLE_MAX_IMAGES_ASSETS_GROUP
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "imageUrls"],
      message: i18next.t(FIELD_MAX_IMAGES_VALID, {
        value: ADS_CAMPAIGN_GOOGLE_MAX_IMAGES_ASSETS_GROUP,
      }),
    });
  }

  if (assetsGroup.imageUrls) {
    assetsGroup.imageUrls.forEach((item, idx) => {
      if (!item) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "imageUrls", idx],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
    });

    const imageUrlsNormalized = assetsGroup.imageUrls
      .map((url) => url?.trim().toLowerCase())
      .filter(Boolean);
    const imageUrlsMap = getIndicesMap(imageUrlsNormalized);

    const hasRepeatedUrls = Object.values(imageUrlsMap).some(
      (indexes) => indexes.length > 1
    );

    if (hasRepeatedUrls) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "imageUrls"],
        message: i18next.t(FIELD_IMAGES_REPEATED),
      });
    }

    Object.entries(imageUrlsMap).forEach(([, indexes]) => {
      if (indexes.length > 1) {
        indexes.forEach((i) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "imageUrls", i],
            message: i18next.t(FIELD_IMAGES_REPEATED),
          });
        });
      }
    });
  }

  // videoUrls
  if (
    assetsGroup?.videoUrls?.length > ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "videoUrls"],
      message: i18next.t(FIELD_MAX_VIDEOS_YOUTUBE_VALID, {
        value: ADS_CAMPAIGN_GOOGLE_MAX_VIDEOS_ASSETS_GROUP,
      }),
    });
  }

  if (assetsGroup.videoUrls) {
    assetsGroup.videoUrls.forEach((item, idx) => {
      if (!item) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "videoUrls", idx],
          message: i18next.t(FIELD_REQUIRED),
        });
      } else if (!REGEX_YOUTUBE_URL.test(item)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "videoUrls", idx],
          message: i18next.t(FIELD_VIDEO_YOUTUBE_URL_INVALID),
        });
      }
    });

    const videoUrlsNormalized = assetsGroup.videoUrls
      .map((url) => url?.trim().toLowerCase())
      .filter(Boolean);
    const videoUrlsMap = getIndicesMap(videoUrlsNormalized);

    Object.entries(videoUrlsMap).forEach(([, indexes]) => {
      if (indexes.length > 1) {
        indexes.forEach((i) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "videoUrls", i],
            message: i18next.t(FIELD_VIDEOS_REPEATED),
          });
        });
      }
    });
  }

  // searchThemes
  if (assetsGroup.searchThemes) {
    if (
      assetsGroup.searchThemes.length > ADS_CAMPAIGN_GOOGLE_MAX_SEARCH_THEMES
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "searchThemes"],
        message: i18next.t(FIELD_MAX_TEXTS_VALID, {
          value: ADS_CAMPAIGN_GOOGLE_MAX_SEARCH_THEMES,
        }),
      });
    }
    const searchThemesWithMoreThan10Words = [];
    const searchThemesWithEmojis = [];
    assetsGroup.searchThemes.forEach((item) => {
      if (item && item.split(" ").length > 10) {
        searchThemesWithMoreThan10Words.push(item);
      }
      if (item && !validateNoEmojis(item)) {
        searchThemesWithEmojis.push(item);
      }
    });

    if (searchThemesWithMoreThan10Words.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "searchThemes"],
        message: i18next.t(FIELD_SEARCH_THEMES_MAX_WORDS, {
          value: 10,
          items: searchThemesWithMoreThan10Words
            .map((item) => `"${item}"`)
            .join(", "),
        }),
      });
    }

    if (searchThemesWithEmojis.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "searchThemes"],
        message: i18next.t(FIELD_EMOJI_NOT_ALLOWED),
      });
    }
  }

  // Validación de longHeadlines
  if (
    !assetsGroup.longHeadlines ||
    assetsGroup.longHeadlines.length < ADS_CAMPAIGN_GOOGLE_MIN_LONG_HEADLINES
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "longHeadlines"],
      message: i18next.t(FIELD_MIN_TEXTS_VALID, {
        value: ADS_CAMPAIGN_GOOGLE_MIN_LONG_HEADLINES,
      }),
    });
  } else if (
    assetsGroup.longHeadlines.length > ADS_CAMPAIGN_GOOGLE_MAX_LONG_HEADLINES
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "longHeadlines"],
      message: i18next.t(FIELD_MAX_TEXTS_VALID, {
        value: ADS_CAMPAIGN_GOOGLE_MAX_LONG_HEADLINES,
      }),
    });
  }
  assetsGroup.longHeadlines?.forEach((item, idx) => {
    if (!item.value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "longHeadlines", idx, "value"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (
      item?.value?.length >
      ADS_CAMPAIGN_PLATFORM_LONG_HEADLINE_MAX_LENGTH[GOOGLE]
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "longHeadlines", idx, "value"],
        message: i18next.t(FIELD_MAX_LENGTH, {
          value: ADS_CAMPAIGN_PLATFORM_LONG_HEADLINE_MAX_LENGTH[GOOGLE],
        }),
      });
    }
    if (item.value && THREE_UPPERCASE_REGEX.test(item.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "longHeadlines", idx, "value"],
        message: i18next.t(FIELD_CONSECUTIVE_UPPERCASE, { value: 3 }),
      });
    }
    if (item.value && !validateNoEmojis(item.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "longHeadlines", idx, "value"],
        message: i18next.t(FIELD_EMOJI_NOT_ALLOWED),
      });
    }
  });

  // Validación de descriptions
  if (
    !assetsGroup.descriptions ||
    assetsGroup.descriptions.length < ADS_CAMPAIGN_GOOGLE_MIN_DESCRIPTIONS
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "descriptions"],
      message: i18next.t(FIELD_MIN_TEXTS_VALID, {
        value: ADS_CAMPAIGN_GOOGLE_MIN_DESCRIPTIONS,
      }),
    });
  } else if (
    assetsGroup.descriptions.length > ADS_CAMPAIGN_GOOGLE_MAX_DESCRIPTIONS
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "descriptions"],
      message: i18next.t(FIELD_MAX_TEXTS_VALID, {
        value: ADS_CAMPAIGN_GOOGLE_MAX_DESCRIPTIONS,
      }),
    });
  }
  assetsGroup.descriptions?.forEach((item, idx) => {
    if (!item.value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "descriptions", idx, "value"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (
      item?.value?.length > ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[GOOGLE]
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "descriptions", idx, "value"],
        message: i18next.t(FIELD_MAX_LENGTH, {
          value: ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[GOOGLE],
        }),
      });
    }
    if (item.value && THREE_UPPERCASE_REGEX.test(item.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "descriptions", idx, "value"],
        message: i18next.t(FIELD_CONSECUTIVE_UPPERCASE, { value: 3 }),
      });
    }
    if (item.value && !validateNoEmojis(item.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "descriptions", idx, "value"],
        message: i18next.t(FIELD_EMOJI_NOT_ALLOWED),
      });
    }
  });
  // Validación especial para el primer description
  if (
    assetsGroup.descriptions &&
    assetsGroup.descriptions[0] &&
    assetsGroup.descriptions[0].value?.length >
      ADS_CAMPAIGN_GOOGLE_FIRST_DESCRIPTION_MAX_LENGTH
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "descriptions", 0, "value"],
      message: i18next.t(FIELD_MAX_LENGTH, {
        value: ADS_CAMPAIGN_GOOGLE_FIRST_DESCRIPTION_MAX_LENGTH,
      }),
    });
  }

  // Validación de titles
  if (
    !assetsGroup.titles ||
    assetsGroup.titles.length < ADS_CAMPAIGN_GOOGLE_MIN_TITLES
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "titles"],
      message: i18next.t(FIELD_MIN_TEXTS_VALID, {
        value: ADS_CAMPAIGN_GOOGLE_MIN_TITLES,
      }),
    });
  } else if (assetsGroup.titles.length > ADS_CAMPAIGN_GOOGLE_MAX_TITLES) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "titles"],
      message: i18next.t(FIELD_MAX_TEXTS_VALID, {
        value: ADS_CAMPAIGN_GOOGLE_MAX_TITLES,
      }),
    });
  }
  assetsGroup.titles?.forEach((item, idx) => {
    if (!item.value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "titles", idx, "value"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
    if (item?.value?.length > ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[GOOGLE]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "titles", idx, "value"],
        message: i18next.t(FIELD_MAX_LENGTH, {
          value: ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[GOOGLE],
        }),
      });
    }
    if (item.value && THREE_UPPERCASE_REGEX.test(item.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "titles", idx, "value"],
        message: i18next.t(FIELD_CONSECUTIVE_UPPERCASE, { value: 3 }),
      });
    }
    if (item.value && !validateNoEmojis(item.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "titles", idx, "value"],
        message: i18next.t(FIELD_EMOJI_NOT_ALLOWED),
      });
    }
  });
  // Validación especial para el primer title
  if (
    assetsGroup.titles &&
    assetsGroup.titles[0] &&
    assetsGroup.titles[0].value?.length >
      ADS_CAMPAIGN_GOOGLE_FIRST_TITLE_MAX_LENGTH
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "titles", 0, "value"],
      message: i18next.t(FIELD_MAX_LENGTH, {
        value: ADS_CAMPAIGN_GOOGLE_FIRST_TITLE_MAX_LENGTH,
      }),
    });
  }

  // Validación de textos repetidos para longHeadlines
  if (assetsGroup.longHeadlines && assetsGroup.longHeadlines.length > 0) {
    const longHeadlinesNormalized = assetsGroup.longHeadlines
      .map((item) => item.value?.trim().toLowerCase())
      .filter(Boolean);
    const longHeadlinesMap = getIndicesMap(longHeadlinesNormalized);

    Object.entries(longHeadlinesMap).forEach(([, indexes]) => {
      if (indexes.length > 1) {
        indexes.forEach((i) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "longHeadlines", i, "value"],
            message: i18next.t(FIELD_TEXT_REPEATED),
          });
        });
      }
    });
  }

  // Validación de textos repetidos para descriptions
  if (assetsGroup.descriptions && assetsGroup.descriptions.length > 0) {
    const descriptionsNormalized = assetsGroup.descriptions
      .map((item) => item.value?.trim().toLowerCase())
      .filter(Boolean);
    const descriptionsMap = getIndicesMap(descriptionsNormalized);

    Object.entries(descriptionsMap).forEach(([, indexes]) => {
      if (indexes.length > 1) {
        indexes.forEach((i) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "descriptions", i, "value"],
            message: i18next.t(FIELD_TEXT_REPEATED),
          });
        });
      }
    });
  }

  // Validación de textos repetidos para titles
  if (assetsGroup.titles && assetsGroup.titles.length > 0) {
    const titlesNormalized = assetsGroup.titles
      .map((item) => item.value?.trim().toLowerCase())
      .filter(Boolean);
    const titlesMap = getIndicesMap(titlesNormalized);

    Object.entries(titlesMap).forEach(([, indexes]) => {
      if (indexes.length > 1) {
        indexes.forEach((i) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "titles", i, "value"],
            message: i18next.t(FIELD_TEXT_REPEATED),
          });
        });
      }
    });
  }
};
